import type { LanguageT } from "@/app/types/languages";

/**
 * Language IDs for priority sorting
 */
const GERMAN_LANGUAGE_ID = "dc27c284-9c4c-40dd-a4d3-e2dbba982ad9";
const ENGLISH_LANGUAGE_ID = "ceff6190-efd7-492c-88fb-af42a55d5a04";

type LanguageWithImageT = LanguageT & {
  image: { url: string | null } | null;
};

/**
 * Sort function for languages with priority ordering:
 * 1. German (always first)
 * 2. English (always second)
 * 3. All other languages alphabetically
 *
 * @param a - First language to compare
 * @param b - Second language to compare
 * @returns Comparison result for Array.sort()
 *
 * @example
 * ```tsx
 * const sortedLanguages = languages.slice().sort(sortLanguages);
 * ```
 */
export function sortLanguages(
  a: LanguageWithImageT,
  b: LanguageWithImageT
): number {
  const aIsGerman = a.id === GERMAN_LANGUAGE_ID;
  const bIsGerman = b.id === GERMAN_LANGUAGE_ID;
  const aIsEnglish = a.id === ENGLISH_LANGUAGE_ID;
  const bIsEnglish = b.id === ENGLISH_LANGUAGE_ID;

  // German has highest priority
  if (aIsGerman && !bIsGerman) return -1;
  if (!aIsGerman && bIsGerman) return 1;

  // English has second priority (only if neither is German)
  if (aIsEnglish && !bIsEnglish && !bIsGerman) return -1;
  if (!aIsEnglish && bIsEnglish && !aIsGerman) return 1;

  // All others alphabetically
  return a.name.localeCompare(b.name);
}
